import React, { useState, useEffect, useMemo } from 'react';
import { Box } from '@mui/material';
import { styled } from '@mui/material/styles';
import ReadMoreButton from '../ReadMoreButton';
import Tech1 from '../../assets/images/hydrogen_value1.jpeg';
import Tech2 from '../../assets/images/hydrogen_value2.jpeg';
import Tech3 from '../../assets/images/hydrogen_value3.jpg';
import Tech4 from '../../assets/images/hydrogen_value4.jpeg';

const images = [Tech1, Tech2, Tech3, Tech4];

const ImageSection = styled(Box)(({ theme }) => ({
  flex: '1 1 auto',
  overflow: 'hidden',
  position: 'relative',
  height: '450px',
  [theme.breakpoints.down('lg')]: {
    height: '400px',
  },
  [theme.breakpoints.down('md')]: {
    height: '350px',
    width: '100%',
  },
  [theme.breakpoints.down('sm')]: {
    height: '300px',
  }
}));

const ImageSlider = styled(Box)(({ ishovered, isresetting }) => ({
  display: 'flex',
  position: 'absolute',
  height: '100%',
  gap: '24px',
  transition: isresetting === 'true' ? 'none' : 'transform 0.5s linear',
  '& img': {
    height: '100%',
    objectFit: 'cover',
    transition: 'all 0.3s ease',
    transform: ishovered === 'true' ? 'scale(1.05)' : 'scale(1)',
  }
}));

const TechLanding = () => {
  const [isHovered, setIsHovered] = useState(false);
  const [position, setPosition] = useState(0);
  const [isResetting, setIsResetting] = useState(false);

  // create a new array with 3 times the images
  const displayImages = useMemo(() => [...images, ...images, ...images], []);

  useEffect(() => {
    let animationFrame;
    
    const animate = () => {
      if (!isHovered) {
        setPosition((prev) => {
          if (-prev >= 100) {
            setIsResetting(true);
            setTimeout(() => {
              setIsResetting(false);
            }, 0);
            return 0;
          }
          return prev - 0.02; // Ajust the speed of the animation
        });
      }
      animationFrame = requestAnimationFrame(animate);
    };

    animationFrame = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(animationFrame);
  }, [isHovered]);

  return (
    <div className='w-full flex flex-col md:flex-row px-6 sm:px-8 md:px-10 lg:px-20 my-10 sm:my-12 md:my-16 lg:my-20'>
      {/* Left side content */}
      <div className='w-full md:w-1/3 relative overflow-hidden mb-8 md:mb-0'>
        <h2 className="text-lg md:text-xl lg:text-2xl font-bold mb-2 md:mb-4 text-theme-blue">
          Our Technology
        </h2>
        <div className="text-xl md:text-2xl lg:text-3xl font-bold mb-2 md:mb-4 text-gray-700">
          Revolutionizing green energy with our advanced electrolyser technology
        </div>
        <div className="space-y-2 md:space-y-4 text-sm md:text-base lg:text-lg text-gray-700">
          <p>
            Designed for efficiency and reliability, it outperforms conventional systems and meets both today's and tomorrow's
            needs. 
          </p>
          <ul className="list-disc pl-8">
            <li>Durable</li>
            <li>Efficient</li>
            <li>Low-cost</li>
            <li>Scalable</li>
          </ul>
        </div>
        <div className="mb-2 md:mb-4">
          <ReadMoreButton to="/technology/green-molecules-electrolysers" />
        </div>
      </div>

      {/* Right moving images */}
      <div className="w-full md:w-2/3 relative overflow-hidden ml-0 md:ml-6 my-auto">
      <ImageSection
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <ImageSlider
          ishovered={isHovered.toString()}
          isresetting={isResetting.toString()}
          style={{
            transform: `translateX(${position}%)`,
            width: '500%', // the width of the slider is 4 times the width of the container
          }}
        >
          {displayImages.map((image, index) => (
            <img
              key={index}
              src={image}
              alt={`Green Molecules Product ${(index % images.length) + 1}`}
              style={{
                width: `calc((100% - ${(displayImages.length - 1) * 24}px) / ${displayImages.length * 0.8})`,
              }}
              onError={(e) => {
                e.target.src = 'https://via.placeholder.com/800x500';
              }}
            />
          ))}
        </ImageSlider>
      </ImageSection>
      </div>
    </div>
  );
};

export default TechLanding;